import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../cn.js';

export interface DropdownMenuItem {
  label: string;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}

export interface DropdownMenuProps {
  trigger: ReactNode;
  items: DropdownMenuItem[];
}

/** Fixed rather than measured — see the component doc comment for why. Wide enough for the
 *  longest label this app currently passes ("Submit for approval"). */
const MENU_WIDTH = 192;
const ITEM_HEIGHT = 32;
const MENU_VERTICAL_PADDING = 8;
/** The menu scrolls internally past this share of the viewport height (`max-h-[70dvh]` below). */
const MENU_MAX_HEIGHT_RATIO = 0.7;
const VIEWPORT_MARGIN = 8;

/**
 * A small dependency-free popover for row actions. No Radix dependency exists in `@hrms/ui` —
 * `apps/web`'s local shadcn/Radix component set is a known divergence from this package's tokens,
 * not the pattern to copy. Uncontrolled: owns its own open state, closes on outside click/Escape.
 *
 * PORTALED to `document.body`, positioned from the trigger's own `getBoundingClientRect()` —
 * deliberately NOT rendered in-place with `position: absolute`. This component is used for row
 * actions inside `Table.tsx`, which wraps its rows in an `overflow-auto` scroll container with a
 * `position: sticky` pinned-right actions column. An in-flow absolute popover there gets silently
 * clipped by the scroll container's overflow box the instant the table hasn't been scrolled to
 * reveal the popover's full rendered width past the pinned column — which is precisely the
 * right-edge trigger this component sits in. Portaling sidesteps every ancestor's overflow and
 * stacking context, the same reason Radix/Headless UI/Floating UI all do it. `MENU_WIDTH` is a
 * fixed constant rather than a DOM measurement so the position can be computed in one pass, before
 * the menu is even in the document — no measure-then-reposition flicker.
 */
export function DropdownMenu({ trigger, items }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuHeight = Math.min(
      items.length * ITEM_HEIGHT + MENU_VERTICAL_PADDING,
      viewportHeight * MENU_MAX_HEIGHT_RATIO,
    );

    // Right-align to the trigger by default (matches the old `right-0` behavior); flip to
    // left-aligned only if right-aligning would push the menu off the LEFT edge — a narrow window
    // or a trigger near x=0, the one case a pure right-anchor can't handle either.
    let left = rect.right - MENU_WIDTH;
    if (left < VIEWPORT_MARGIN) left = rect.left;
    left = Math.min(Math.max(left, VIEWPORT_MARGIN), viewportWidth - MENU_WIDTH - VIEWPORT_MARGIN);

    // Open downward by default; flip upward if there isn't room below (a row near the bottom of
    // the viewport, which a pinned-right action column in a tall table hits constantly).
    let top = rect.bottom + 4;
    if (top + menuHeight > viewportHeight - VIEWPORT_MARGIN) top = rect.top - menuHeight - 4;

    setPosition({ top, left });
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      const insideTrigger = triggerRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    // Closes on scroll rather than tracking every ancestor's scroll offset to reposition live — a
    // row-action menu is a quick "click, act, close" interaction, not something worth keeping
    // open across a scroll. `capture: true` on `window` catches scroll on any scrollable
    // ancestor (e.g. Table.tsx's own overflow-auto container), not just the window itself — scroll
    // events don't bubble, but they are still seen during the capture phase.
    function onScroll(event: Event) {
      // A long menu scrolls internally — that must not close it.
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {trigger}
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: 'fixed', top: position.top, left: position.left, width: MENU_WIDTH }}
            className="z-40 max-h-[70dvh] overflow-y-auto rounded-md border border-border bg-surface-raised py-1 shadow-lg"
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                className={cn(
                  'flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-bg-subtle',
                  'disabled:pointer-events-none disabled:opacity-50',
                  item.tone === 'danger' ? 'text-danger' : 'text-fg',
                )}
              >
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
